import React, { useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import Link from '@dashboard/components/Link';
import { Button } from '@dashboard/components/ui/button';
import { Label } from '@dashboard/components/ui/label';
import { Switch } from '@dashboard/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@dashboard/components/ui/tooltip';
import { getLinkInBioHomepageUrl, getLinkInBioShortUrl } from '@dashboard/services/getLinkInBioUrl';
import { fromLinkInBioHomepage, toLinkInBioHomepage } from '../homepage';
import SectionCard from './SectionCard';

export default function LinkSettings({ savedPublished }: { savedPublished: boolean }) {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const [isUpdatingHomepage, setIsUpdatingHomepage] = useState(false);

  const shortUrl = getLinkInBioShortUrl(seller);
  const isHomepage = seller.homepage?.type === 'linkInBio';

  if (!shortUrl) {
    return (
      <SectionCard title="Set your store URL first">
        <p className="text-sm text-muted-foreground">
          Your link in bio lives at your store URL.{' '}
          <Link to="/settings/domains">Set your store URL</Link>
        </p>
      </SectionCard>
    );
  }

  const onHomepageChange = async (checked: boolean) => {
    setIsUpdatingHomepage(true);
    const homepage = checked
      ? toLinkInBioHomepage(seller.homepage)
      : fromLinkInBioHomepage(seller.homepage);
    const { error } = await updateSeller({ homepage });
    setIsUpdatingHomepage(false);

    if (error) {
      toast({ title: 'Could not change your homepage', type: 'error' });
      return;
    }

    toast({
      title: checked ? 'Your link in bio is now your store homepage' : 'Homepage changed back',
      type: 'success',
    });
  };

  const needsPublish = !isHomepage && !savedPublished;
  const homepageSwitch = (
    <Switch
      id="link-in-bio-homepage"
      checked={isHomepage}
      disabled={isUpdatingHomepage || needsPublish}
      onCheckedChange={onHomepageChange}
    />
  );

  return (
    <>
      <SectionCard
        title="Your link"
        description="Your link in bio uses your store URL, which is also your subdomain."
      >
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-1.5">
            <span className="min-w-0 flex-1 truncate text-sm">{shortUrl}</span>
            <Button variant="ghost" size="sm" to="/settings/domains">
              Change
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            If you change it, update your link everywhere you shared it.
          </p>
        </div>
      </SectionCard>

      <SectionCard title="Homepage">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="link-in-bio-homepage">Use as your store homepage</Label>
            {savedPublished && (
              <p className="text-sm text-muted-foreground">
                Show your link in bio at {getLinkInBioHomepageUrl(seller)}.
              </p>
            )}
          </div>
          {needsPublish ? (
            <TooltipProvider>
              <Tooltip>
                {/* A disabled switch fires no pointer events, so the trigger needs a wrapper. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex" tabIndex={0}>
                    {homepageSwitch}
                  </span>
                </TooltipTrigger>
                <TooltipContent>Publish your link in bio first</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            homepageSwitch
          )}
        </div>
      </SectionCard>
    </>
  );
}
